import { useMemo, useContext } from "react";
import { StyleSheet, View, Text } from "react-native";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

import { appColors, tooltipShadow } from "../constants/colors";
import { CalculationsContext } from "../store/calculationsCtx";
import ChartLayout from "./UI/ChartLayout";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <View style={tooltip.box}>
      <Text style={tooltip.month}>Month {label}</Text>
      {payload.map((entry, i) => (
        <View key={i} style={tooltip.row}>
          <View style={[tooltip.dot, { backgroundColor: entry.color }]} />
          <Text style={tooltip.label}>{entry.name}</Text>
          <Text style={tooltip.value}>
            {Number(entry.value).toLocaleString(undefined, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </Text>
        </View>
      ))}
    </View>
  );
};

const tooltip = StyleSheet.create({
  box: {
    backgroundColor: appColors.cardBg,
    borderRadius: 8,
    borderWidth: 0.5,
    borderColor: appColors.border,
    paddingVertical: 8,
    paddingHorizontal: 12,
    gap: 4,
    ...tooltipShadow,
  },
  month: { fontSize: 10, color: appColors.textSecondary, marginBottom: 2 },
  row: { flexDirection: "row", alignItems: "center", gap: 6 },
  dot: { width: 8, height: 8, borderRadius: 4 },
  label: { flex: 1, fontSize: 11, color: appColors.textSecondary },
  value: { fontSize: 11, fontWeight: "500", color: appColors.textPrimary },
});

const formatYAxis = value => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return value;
};

function MixedChart() {
  const calculationsCtx = useContext(CalculationsContext);
  const lineChart = calculationsCtx.results2?.schedule;

  const chartData = useMemo(() => {
    if (!lineChart || lineChart.length === 0) return [];

    let cumulativePrincipal = 0;
    let cumulativeInterest = 0;

    return lineChart.map(item => {
      const p = parseFloat(item.principal) || 0;
      const i = parseFloat(item.interest) || 0;
      const b = parseFloat(item.remainingBalance) || 0;

      cumulativePrincipal += p;
      cumulativeInterest += i;

      return {
        month: item.term,
        Principal: Math.round(cumulativePrincipal),
        "Total paid": Math.round(cumulativePrincipal + cumulativeInterest),
        Balance: Math.round(b),
      };
    });
  }, [lineChart]);

  if (!lineChart || chartData.length === 0) return null;

  // Only show a tick every N months to avoid crowding
  const tickInterval = Math.ceil(chartData.length / 6);
  const xTicks = chartData.filter((_, i) => i % tickInterval === 0 || i === chartData.length - 1).map(d => d.month);

  return (
    <ChartLayout>
      <View style={styles.header}>
        <Text style={styles.title}>Payment breakdown</Text>
      </View>

      {/* Legend */}
      <View style={styles.legend}>
        {[
          { label: "Principal", color: appColors.chartPrincipal },
          { label: "Total paid", color: appColors.chartInterest },
          { label: "Balance", color: appColors.chartBalance },
        ].map(item => (
          <View key={item.label} style={styles.legendItem}>
            <View style={[styles.swatch, { backgroundColor: item.color }]} />
            <Text style={styles.legendLabel}>{item.label}</Text>
          </View>
        ))}
      </View>

      <View style={styles.chartWrap}>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="gradTotalPaid" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={appColors.chartInterest} stopOpacity={0.85} />
                <stop offset="95%" stopColor={appColors.chartInterest} stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="gradPrincipal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={appColors.chartPrincipal} stopOpacity={0.85} />
                <stop offset="95%" stopColor={appColors.chartPrincipal} stopOpacity={0.1} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="month"
              ticks={xTicks}
              tick={{ fontSize: 10, fill: appColors.textMuted }}
              axisLine={false}
              tickLine={false}
              label={{ value: "Month", position: "insideBottom", offset: -2, fontSize: 10, fill: appColors.textMuted }}
              height={32}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 10, fill: appColors.textMuted }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip content={<CustomTooltip />} />

            <Area
              type="monotone"
              dataKey="Total paid"
              stroke={appColors.chartInterest}
              strokeWidth={2}
              fill="url(#gradTotalPaid)"
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Area
              type="monotone"
              dataKey="Principal"
              stroke={appColors.chartPrincipal}
              strokeWidth={2}
              fill="url(#gradPrincipal)"
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Area
              type="monotone"
              dataKey="Balance"
              stroke={appColors.chartBalance}
              strokeWidth={2}
              strokeDasharray="5 4"
              fill="none"
              dot={false}
              activeDot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </View>
    </ChartLayout>
  );
}

export default MixedChart;

const styles = StyleSheet.create({
  header: {
    width: "100%",
    marginBottom: 8,
  },

  title: {
    fontSize: 12,
    fontWeight: "500",
    color: appColors.textPrimary,
  },

  legend: {
    flexDirection: "row",
    gap: 14,
    marginBottom: 4,
    flexWrap: "wrap",
  },

  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },

  swatch: {
    width: 10,
    height: 10,
    borderRadius: 3,
  },

  legendLabel: {
    fontSize: 10,
    color: appColors.textSecondary,
  },

  chartWrap: {
    width: "100%",
  },
});
